import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { useCreateCaseMutation } from '../../../api/generated/case-controller/case-controller.ts';
import {
  type IPersonListItem,
  searchPersonsForAutocomplete,
} from '../../../api/generated/person-controller/person-controller.ts';
import { GenericAutocomplete } from '../../components/GenericAutocomplete.tsx';
import { CreatePersonData } from '../../masterdata/components/CreatePersonData.tsx';

interface FormValues {
  caseNumber: string | null;
  selectedPerson: IPersonListItem | null;
}

const CREATE_PERSON_OPTION_ID = '__create_person__';

type CreatePersonOption = IPersonListItem & {
  id: typeof CREATE_PERSON_OPTION_ID;
};

type PersonOption = IPersonListItem | CreatePersonOption;

const CREATE_PERSON_OPTION: CreatePersonOption = {
  id: CREATE_PERSON_OPTION_ID,
  firstName: 'Neue Person erstellen',
  lastName: '',
  birthDate: '',
  gender: null,
  created: null,
  phone: null,
  email: null,
};

function isCreatePersonOption(option: PersonOption | null): option is CreatePersonOption {
  return option?.id === CREATE_PERSON_OPTION_ID;
}

export function CreateCase({
  open,
  onClose,
  preselectedPerson,
  lockPerson = false,
}: {
  open: boolean;
  onClose: () => void;
  isIndexPatient: boolean;
  preselectedPerson?: { id: string; firstName?: string | null; lastName?: string | null } | null;
  lockPerson?: boolean;
}) {
  const navigate = useNavigate();
  const [isCreatePersonOpen, setIsCreatePersonOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      caseNumber: null,
      selectedPerson: preselectedPerson
        ? {
          id: preselectedPerson.id,
          firstName: preselectedPerson.firstName ?? '',
          lastName: preselectedPerson.lastName ?? '',
          birthDate: '',
          gender: null,
          created: null,
          phone: null,
          email: null,
        }
        : null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        caseNumber: null,
        selectedPerson: preselectedPerson
          ? {
            id: preselectedPerson.id,
            firstName: preselectedPerson.firstName ?? '',
            lastName: preselectedPerson.lastName ?? '',
            birthDate: '',
            gender: null,
            created: null,
            phone: null,
            email: null,
          }
          : null,
      });
    }
  }, [open, preselectedPerson?.id, reset]);

  const createCaseMutation = useCreateCaseMutation({
    onSuccess: (patientId) => navigate({ to: '/patients/$patientId', params: { patientId } }),
    onError: () => toast.error('Beim Erstellen des Patienten ist ein Fehler aufgetreten'),
  });

  const onSubmit = handleSubmit((data) => {
    createCaseMutation.mutate({
      caseNumber: data.caseNumber,
      personId: data.selectedPerson?.id ?? null,
    });
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Neuen Fall erfassen</Typography>

          <form onSubmit={onSubmit}>
            <Stack spacing={2}>
              <FormGrid>
                <FormControl error={!!errors.caseNumber}>
                  <FormLabel>
                    <Typography>Fallnummer</Typography>
                  </FormLabel>

                  <Input type="text" {...register('caseNumber')} />

                  {errors.caseNumber && (
                    <FormHelperText>{errors.caseNumber.message}</FormHelperText>
                  )}
                </FormControl>

                <FormControl error={!!errors.selectedPerson}>
                  <FormLabel>
                    <Typography>Indexpatient</Typography>
                  </FormLabel>

                  <Controller
                    name="selectedPerson"
                    control={control}
                    // rules={{ required: 'Indexpatient muss ausgewählt werden' }}
                    render={({ field: { value, onChange } }) => (
                      <GenericAutocomplete<PersonOption>
                        value={value}
                        onChange={(selectedPerson) => {
                          if (isCreatePersonOption(selectedPerson)) {
                            setIsCreatePersonOpen(true);
                            return;
                          }

                          onChange(selectedPerson);
                        }}
                        fetchOptions={async ({ search, page, pageSize, signal }) => {
                          const response = await searchPersonsForAutocomplete(search, {
                            page,
                            size: pageSize,
                            signal,
                          });

                          return {
                            ...response,
                            items: page === 0 ? [CREATE_PERSON_OPTION, ...response.items] : response.items,
                          };
                        }}
                        getOptionLabel={(option) =>
                          isCreatePersonOption(option)
                            ? 'Neue Person erstellen'
                            : `${option.firstName} ${option.lastName}`.trim()
                        }
                        getOptionKey={(option) => option.id}
                        getSearchText={(option) =>
                          isCreatePersonOption(option)
                            ? 'Neue Person erstellen'
                            : `${option.firstName ?? ''} ${option.lastName ?? ''} ${option.email ?? ''} ${option.phone ?? ''}`
                        }
                        isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                        renderOptionContent={(option) => (
                          <Typography level={isCreatePersonOption(option) ? 'title-md' : 'title-sm'}>
                            {isCreatePersonOption(option)
                              ? 'Neue Person erstellen'
                              : `${option.firstName} ${option.lastName}`.trim()}
                          </Typography>
                        )}
                        enableSearch
                        enableLazyLoad
                        placeholder="Bitte auswählen"
                        disabled={lockPerson}
                        noOptionsText="Keine Personen gefunden"
                      />
                    )}
                  />

                  {errors.selectedPerson && <FormHelperText>{errors.selectedPerson.message}</FormHelperText>}
                </FormControl>
              </FormGrid>

              <FormActionButtons
                onAbortClick={handleClose}
                // disableSubmit={!isFormDirty || !isValid}
                isLoading={createCaseMutation.isPending}
              />
            </Stack>
          </form>
        </ModalDialog>
      </Modal>

      <CreatePersonData
        open={isCreatePersonOpen}
        onClose={() => setIsCreatePersonOpen(false)}
        onCreated={(person) => {
          setValue('selectedPerson', person, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
      />
    </>
  );
}

const FormGrid = styled('div')`
  width: 700px;
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
